import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import Table from "../../newUXComponents/Table";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { refreshUserPermissions } from "../../redux/modules/auth/actions";
import { notification, Tag, Tooltip } from "antd";
import api from "../../service";
import {
  fetchDocumentsList,
  setDocumentsList,
} from "../../redux/modules/documentApproval/actions";
import { StatusDiv } from "../../pages/AdminDocuments/ApprovalDocuments/styles";
import { FiEye } from "react-icons/fi";
import customParseFormat from "dayjs/plugin/customParseFormat";
import utc from "dayjs/plugin/utc";
import dayjs from "dayjs";
import QuickPreviewModal from "../../pages/AdminDocuments/components/QuickPreviewModal";
import { useLoading } from "../../hooks/loading";
import RejectDocumentModal from "../../pages/AdminDocuments/components/RejectDocumentModal";

dayjs.extend(customParseFormat);
dayjs.extend(utc);

const DocumentApproval = (activeTab) => {
  const initialStateObjectDocument = {
    id: 0,
    urlForDownload: "",
    urlForView: "",
    title: "",
    subtitle: "",
    dateText: "",
    status: "",
    justification: "",
    observation: "",
    rejectionReason: null,
    flowName: "",
  };
  const [tablePagination, setTablePagination] = useState({
    current: 0,
    defaultCurrent: 0,
    pageSize: 10,
    showQuickJumper: true,
    total: 0,
  });
  const intl = useIntl();
  const { token, user, permission } = useSelector((state) => state.auth);
  const { event, contract } = useSelector((state) => state.initialSettings);
  const { handleLoading } = useLoading();
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [objectDocument, setObjectDocument] = useState(
    initialStateObjectDocument
  );
  const [previewOpen, setPreviewOpen] = useState(false);
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const isStaff = user?.role?.type === "STAFF";
  const hasPermissionToDeleteOwnDocuments = permission.includes(
    "STAFF_DELETE_UPLOADED_DOCUMENTS"
  );
  const permissionsLoadedForModalRef = useRef(false);

  // Função para recarregar permissões do usuário
  const refreshPermissions = useCallback(async () => {
    if (!token) return;

    try {
      // Buscar permissões atualizadas do usuário
      const response = await api.get("/users/permissions", {
        headers: {
          "Content-Type": "application/json",
          Accept: "*/*",
          Authorization: `Bearer ${token}`,
        },
      });

      // Atualizar permissões no Redux
      if (!response.data?.error && response.data?.permissionCategories) {
        // Criar objeto user atualizado com as novas permissionCategories
        const updatedUser = {
          ...user,
          permissionCategories: response.data.permissionCategories,
        };

        dispatch(refreshUserPermissions({ user: updatedUser }));
      }
    } catch (error) {
      console.error("Erro ao atualizar permissões:", error);
    }
  }, [token, user, dispatch]);

  const headerRequest = useMemo(
    () => ({
      "Content-Type": "application/json",
      Accept: "*/*",
      Authorization: `Bearer ${token}`,
    }),
    [token]
  );

  const documentsList = useSelector(
    (state) => state.approvalDocument.documentsList
  );

  useEffect(() => {
    if (activeTab.activeTab === "3") {
      handleFetchDocuments(0, 10, "");
    }
  }, [event.value, contract.value, activeTab.activeTab]);

  // useEffect para recarregar permissões APENAS UMA VEZ quando o modal abrir
  useEffect(() => {
    if (previewOpen && !permissionsLoadedForModalRef.current) {
      permissionsLoadedForModalRef.current = true;
      refreshPermissions();
    } else if (!previewOpen) {
      // Resetar flag quando o modal fechar para permitir nova carga na próxima abertura
      permissionsLoadedForModalRef.current = false;
    }
  }, [previewOpen, refreshPermissions]);

  const callbackPaginationInfo = useCallback(
    (pagination) => {
      handleLoading(false);
      setTablePagination({
        current: pagination.currentPage + 1,
        defaultCurrent: 1,
        pageSize: pagination.pageSize,
        showQuickJumper: true,
        total: pagination.totalItems,
      });
    },
    [handleLoading]
  );

  const handleFetchDocuments = useCallback(
    (page = 0, size = 10, search = "") => {
      handleLoading(true);

      const params = {
        page,
        size,
        status: null,
        document: null,
        opportunityId: contract.value,
        search,
      };

      dispatch(
        fetchDocumentsList(
          event.value,
          (data) => {
            dispatch(setDocumentsList(data.documents));
            if (data) callbackPaginationInfo(data);
            handleLoading(false);
          },
          (error) => {
            console.error("❌ Erro ao buscar documentos:", error);
            handleLoading(false);
          },
          params
        )
      );
    },
    [
      dispatch,
      event.value,
      callbackPaginationInfo,
      handleLoading,
      contract.value,
    ]
  );

  const handleApproveReject = (objectDoc) => {
    const locale = intl.locale;
    let dateSentUpload = "";

    if (locale === "en" || locale === "en-US")
      dateSentUpload =
        dayjs(objectDoc.fileSubmissionDate).format("M/D/YYYY") ?? "---";
    else
      dateSentUpload =
        dayjs(objectDoc.fileSubmissionDate).format("DD/MM/YYYY") ?? "---";

    setObjectDocument({
      id: objectDoc.id ?? 0,
      urlForDownload: objectDoc.urlForDownload,
      urlForView: objectDoc.urlForView,
      title: objectDoc.fileName || "Documento",
      subtitle: objectDoc.company || "MMP Montagem e Locacao LTDA",
      dateText: dateSentUpload,
      status: objectDoc.status,
      justification: objectDoc.justification,
      observation: objectDoc.observation,
      executionObservation: objectDoc.executionObservation,
      rejectionReason: objectDoc.rejectionReason,
      uploadOrigin: objectDoc.uploadOrigin,
      items: objectDoc.items,
      documentPackage: objectDoc.documentPackage,
      flowName: objectDoc.flowName || "",
    });

    setPreviewOpen(true);
  };

  const columns = [
    {
      title: intl.formatMessage({ id: "document_name" }),
      dataIndex: "fileName",
      key: "fileName",
      width: "20%",
      render: (text) => (
        <Tooltip title={text}>
          <span>
            {text === "document_package"
              ? intl.formatMessage({ id: "document_package" })
              : text || ""}
          </span>
        </Tooltip>
      ),
    },
    {
      title: intl.formatMessage({ id: "approval_documents.company" }),
      dataIndex: "company",
      key: "company",
      width: "20%",
      render: (text) => (
        <Tooltip title={text}>
          <span>{text}</span>
        </Tooltip>
      ),
    },
    {
      title: intl.formatMessage({ id: "approval_documents.company_type" }),
      dataIndex: "companyType",
      key: "companyType",
      width: "20%",
      render: (text) => (
        <Tooltip title={intl.formatMessage({ id: text })}>
          <span>{intl.formatMessage({ id: text })}</span>
        </Tooltip>
      ),
    },

    {
      title: intl.formatMessage({ id: "approval_documents.status" }),
      dataIndex: "status",
      key: "status",
      width: "20%",
      render: (status) => {
        return (
          <StatusDiv status={status}>
            {String(
              intl.formatMessage({
                id:
                  status === "PENDING"
                    ? "admin.documents.pending_approve"
                    : `${status.toLowerCase()}`,
              })
            )}
          </StatusDiv>
        );
      },
    },
    {
      title: intl.formatMessage({ id: "actions" }),
      key: "actions",
      width: "10%",
      style: { backgroundColor: "red" },
      align: "center",
      render: (_, record) => (
        <FiEye
          style={{
            cursor: "pointer",
            fontSize: 25,
            color: "rgba(17, 167, 217, 1)",
          }}
          onClick={() => handleApproveReject(record)}
        />
      ),
    },
  ];

  const cleanModalApproveReject = () => {
    setObjectDocument(initialStateObjectDocument);
    setPreviewOpen(false);
    setRejectModalOpen(false);
  };

  const handleDeletePendingDocument = async (document) => {
    if (!document?.id) return;

    const isPackage =
      Boolean(document.documentPackage) && document.items?.length > 0;

    const documentIds = isPackage
      ? document.items.map((item) => item.documentExecutionId).filter(Boolean)
      : null;

    try {
      setIsLoading(true);

      const response = await api.delete(
        `/upload-flow/documents/${document.id}`,
        {
          headers: headerRequest,
          ...(isPackage && { data: { documentIds } }),
        }
      );

      if (response.status === 200 || response.status === 204) {
        notification.success({
          message: intl.formatMessage({ id: "success" }),
          description: intl.formatMessage({
            id: isPackage
              ? "document_package_deleted_successfully"
              : "document_deleted_successfully",
          }),
        });

        cleanModalApproveReject();
        await handleFetchDocuments();
      }
    } catch (error) {
      notification.error({
        message: intl.formatMessage({ id: "error" }),
        description: intl.formatMessage({ id: "error_deleting_document" }),
      });

      console.error("Error deleting document:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getPackageDocumentIds = useCallback(
    () =>
      objectDocument.documentPackage && objectDocument.items?.length > 0
        ? {
            documentIds: objectDocument.items.map(
              (item) => item.documentExecutionId
            ),
          }
        : {},
    [objectDocument]
  );

  const handleApprove = async () => {
    try {
      setIsLoading(true);
      const DocumentApproveOrRejectStatusRequest = {
        justification: "",
        observation: "",
        approvalStatus: "APPROVED",
        ...getPackageDocumentIds(),
      };

      const response = await api.post(
        `/upload-flow/documents/${objectDocument.id}/review`,
        DocumentApproveOrRejectStatusRequest,
        headerRequest
      );

      if (response.status === 200) {
        notification.success({
          message: intl.formatMessage({ id: "success" }),
          description: intl.formatMessage({
            id: objectDocument.documentPackage
              ? "document_package_approved_successfully"
              : "document_approved_successfully",
          }),
        });

        cleanModalApproveReject();
        await handleFetchDocuments();
      }
    } catch (error) {
      notification.error({
        message: intl.formatMessage({ id: "error" }),
        description: intl.formatMessage({ id: "error" }),
      });

      console.error("Error approving document:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRejectConfirm = async (rejectData) => {
    try {
      setIsLoading(true);
      const DocumentApproveOrRejectStatusRequest = {
        rejectionReasonId: rejectData.rejectionReasonId,
        justification: rejectData.justification,
        observation: rejectData.observation || "",
        approvalStatus: "REJECTED",
        ...getPackageDocumentIds(),
      };

      const response = await api.post(
        `/upload-flow/documents/${objectDocument.id}/review`,
        DocumentApproveOrRejectStatusRequest,
        headerRequest
      );

      if (response.status === 200) {
        notification.success({
          message: intl.formatMessage({ id: "success" }),
          description: intl.formatMessage({
            id: objectDocument.documentPackage
              ? "document_package_rejected_successfully"
              : "document_rejected_successfully",
          }),
        });

        cleanModalApproveReject();
        await handleFetchDocuments();
      }
    } catch (error) {
      notification.error({
        message: intl.formatMessage({ id: "error" }),
        description: intl.formatMessage({ id: "error" }),
      });

      console.error("Error rejecting document:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRejectClick = () => {
    setPreviewOpen(false);
    setRejectModalOpen(true);
  };

  const handleGoBackToPreview = () => {
    setRejectModalOpen(false);
    setPreviewOpen(true);
  };

  return (
    <>
      <QuickPreviewModal
        isClientUX={true}
        open={previewOpen}
        onClose={() => cleanModalApproveReject()}
        onApprove={() => {
          handleApprove();
        }}
        onReject={() => {
          handleRejectClick();
        }}
        onDelete={() => handleDeletePendingDocument(objectDocument)}
        isStaff={isStaff}
        hasPermissionToDeleteOwnDocuments={hasPermissionToDeleteOwnDocuments}
        id={objectDocument.id}
        urlForDownload={objectDocument.urlForDownload}
        urlForView={objectDocument.urlForView}
        title={objectDocument.title}
        subtitle={objectDocument.subtitle}
        dateText={objectDocument.dateText}
        status={objectDocument.status}
        justification={objectDocument.justification}
        executionObservation={objectDocument.executionObservation}
        observation={objectDocument.observation}
        uploadOrigin={objectDocument.uploadOrigin}
        rejectionReason={objectDocument.rejectionReason}
        items={objectDocument.items}
        documentPackage={objectDocument.documentPackage}
        flowName={objectDocument.flowName}
      />

      <Table
        title={intl.formatMessage({ id: "documents" })}
        dataSource={documentsList || []}
        columns={columns}
        tablePagination={tablePagination}
        handleGetInfoTable={handleFetchDocuments}
        rowKey="id"
        withFilters={true}
      />

      <RejectDocumentModal
        isClientUX={true}
        open={rejectModalOpen}
        onClose={() => cleanModalApproveReject()}
        onGoBack={handleGoBackToPreview}
        onConfirm={handleRejectConfirm}
        loading={isLoading}
      />
    </>
  );
};

export default DocumentApproval;
